import { useContext, useState, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Combobox, Row, TruncatedAccount } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import { featureEquals } from "byzantine/src/Feature";
import { groupAccountsByOrganization } from "byzantine";
import { useUserFeatures } from "./contexts/UserFeaturesContext";
import { useInstitutionSettings } from "./contexts/InstitutionSettingsContext";
import AddAccountModal from "./transfer/AddAccountModal";
import AccountContext from "./contexts/AccountContext";
import { VERIFYROUTES } from "./settings/linked_accounts/hooks";
import { useCurrentUser } from "./contexts/CurrentUserContext";

type ComboAccountSelectorProps = {
  showAddExternalAccountLink?: boolean;
  value: string;
  accounts: Account[];
  isDestination: boolean;
  label: string;
  onChange: (accountId: string) => void;
  error?: string;
  errorText?: string;
  basename?: string;
};

export default function ComboAccountSelector({
  showAddExternalAccountLink = true,
  isDestination = false,
  value,
  accounts,
  label,
  onChange,
  error,
  errorText,
  basename,
}: ComboAccountSelectorProps) {
  const { l10n } = useLocalization();
  const userFeatures = useUserFeatures();
  const institutionSettings = useInstitutionSettings();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { accounts: allAccounts, setAccounts } = useContext(AccountContext);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const selectedAccount = accounts.find((a) => a.id === value);

  const getDisplayedValue = () => {
    if (selectedAccount) {
      const relevantBalance = selectedAccount.getRelevantBalance(isDestination);
      return relevantBalance
        ? `${selectedAccount.getShortDescription()}: ${relevantBalance}`
        : selectedAccount.getShortDescription();
    }
    return "";
  };

  const [inputValue, setInputValue] = useState<string>(
    selectedAccount ? getDisplayedValue() : ""
  );

  useLayoutEffect(() => {
    setInputValue(selectedAccount ? getDisplayedValue() : "");
  }, [value, selectedAccount, isDestination]);

  const isSudoRoute = location.pathname.includes("sudo");
  const groupedAccounts = groupAccountsByOrganization(
    accounts,
    currentUser?.org_name
  );
  // more than one organization means we should group
  const shouldGroup =
    groupedAccounts.size > 1 && userFeatures.multientity_aggregation;

  const canAddExternalAccount =
    featureEquals(userFeatures, "indigo.ExternalAccount_permission", "*") &&
    showAddExternalAccountLink &&
    ((!isDestination && institutionSettings.ach_allows_pull) ||
      (isDestination && institutionSettings.ach_allows_push)) &&
    (userFeatures as any).ach;

  const updateAccounts = (changedAccounts: Account[]) => {
    const accountsUpdated = allAccounts.map((account: Account) => account);
    changedAccounts.forEach((acc) => {
      const account = new Account(acc);
      if (account.isExternal() && !account.verified) return;
      const idx = accountsUpdated.findIndex(
        (a: Account) => a.id === account.id
      );
      if (idx !== -1) {
        accountsUpdated[idx] = account;
      } else {
        accountsUpdated.push(account);
      }
    });
    setAccounts(accountsUpdated);
  };

  const handleChange = (accountId: string) => {
    onChange(accountId);
    const pickedAccount = accounts.find((a) => a.id === accountId);
    setInputValue(pickedAccount ? getDisplayedValue() : "");
  };

  const addExternalAccountOnClick = () => {
    setModalOpen(true);
    navigate(
      userFeatures.instant_account_verification
        ? `/${basename}/${VERIFYROUTES.verifyOptions}`
        : `/${basename}/${VERIFYROUTES.manualVerify}`
    );
  };

  const filterItemsByInput = (items: any, inputVal: any) =>
    items.filter((item: any) => {
      const query = (item.props.searchValue || item.props.value).toLowerCase();
      return query.includes(inputVal.toLowerCase());
    });

  return (
    <>
      <Combobox
        label={label}
        value={value}
        onChange={handleChange}
        errorText={errorText || error}
        inputValue={inputValue}
        onInputChange={(updatedValue: string) => {
          // If an account is selected and blur is trying to set searchValue,
          // override it with the proper display value
          if (selectedAccount) {
            const displayedValue = getDisplayedValue();
            setInputValue(displayedValue);
          } else {
            // Allow typing when no selection or when filtering
            setInputValue(updatedValue);
          }
        }}
        clearSelectionOnAction={true}
        filterItemsByInput={filterItemsByInput}
      >
        {shouldGroup
          ? [...groupedAccounts].flatMap(([orgName, accountsByOrg]) => [
              <Combobox.Heading key={`heading-${orgName}`} text={orgName} />,
              ...accountsByOrg.map((account) => {
                const { name, endingValue } =
                  account.getTruncatedValuesWithoutBalance();
                const relevantBalance = account.isExternal()
                  ? "External"
                  : account.getRelevantBalance(isDestination);
                const subLine = [relevantBalance, orgName].filter((a) => (a)).join(" | ");

                return (
                  <Combobox.Item
                    key={account.id}
                    value={account.id}
                    searchValue={`${account.getShortDescription()} ${orgName}`}
                  >
                        <TruncatedAccount name={name} lastFour={endingValue} />
                        {subLine ? (
                          <div className="fontSize--s fontColor--secondary margin--top--xxs">
                            {subLine}
                          </div>
                        ) : null}
                  </Combobox.Item>
                );
              }),
            ])
          : [...accounts].map((account) => {
              const { name, endingValue } =
                account.getTruncatedValuesWithoutBalance();
              const relevantBalance = account.isExternal()
                ? "External"
                : account.getRelevantBalance(isDestination);

              return (
                <Combobox.Item
                  key={account.id}
                  value={account.id}
                  searchValue={account.getShortDescription()}
                >
                  <Row gapSize="xxs">
                    <Row.Item>
                      <TruncatedAccount name={name} lastFour={endingValue} />
                      {relevantBalance ? (
                        <div className="fontSize--s fontColor--secondary margin--top--xxs">
                          {relevantBalance}
                        </div>
                      ) : null}
                    </Row.Item>
                  </Row>
                </Combobox.Item>
              );
            })}

        {canAddExternalAccount && (
          <Combobox.Action
            label={l10n.getString("add-new-external-account")}
            onSelect={addExternalAccountOnClick}
          />
        )}
      </Combobox>
      <AddAccountModal
        open={!isSudoRoute && modalOpen}
        handleClose={() => {
          setModalOpen(false);
          navigate(`/${basename}`);
        }}
        updateAccounts={updateAccounts}
        basename={basename}
      />
    </>
  );
}
